import { sha256 } from "@noble/hashes/sha2.js";
import { deriveKeyMaterial, IDENTITY_SALT } from './key-derivation';
const base64URLStringToBuffer = (base64url) => {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
};
const bufferToBase64URLString = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
/** In-memory fallback storage (non-persistent). */
export class InMemoryPasskeyStorage {
    constructor() {
        this.credentialIds = [];
        this.activeId = null;
    }
    getActiveCredentialId() { return this.activeId; }
    getAllCredentialIds() { return this.credentialIds; }
    storeCredentialId(credId) {
        if (!this.credentialIds.includes(credId)) {
            this.credentialIds.push(credId);
        }
        this.activeId = credId;
    }
}
export const formatPasskeyIdForDisplay = (credId) => {
    if (!credId || credId.length < 8)
        return credId;
    return `id: ${credId.slice(0, 2)}...${credId.slice(-5)}`;
};
export const getPasskeyPrfSalt = () => {
    const input = Buffer.from(`train-passkey-prf-salt-v1:${IDENTITY_SALT}`, 'utf8');
    return new Uint8Array(sha256(input));
};
export const checkPrfSupport = async () => {
    const result = {
        supported: false,
        platformAuthenticatorAvailable: false,
        prfCapabilityReported: null,
    };
    if (typeof window === 'undefined' || !window.isSecureContext || !window.PublicKeyCredential) {
        result.reason = 'WebAuthn not available';
        return result;
    }
    try {
        result.platformAuthenticatorAvailable =
            await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable() ?? false;
    }
    catch { /* ignore */ }
    try {
        const capabilities = await PublicKeyCredential.getClientCapabilities?.();
        if (capabilities && 'prf' in capabilities) {
            result.prfCapabilityReported = capabilities.prf === true;
            if (capabilities.prf === true) {
                result.supported = true;
                return result;
            }
        }
    }
    catch { /* API not available */ }
    const isWindows = /windows/i.test(navigator.userAgent);
    if (isWindows && result.platformAuthenticatorAvailable && result.prfCapabilityReported !== true) {
        result.reason = 'Windows Hello does not support PRF. Use a security key instead.';
        result.platformHint = 'windows_hello_no_prf';
        result.supported = true;
        return result;
    }
    if (result.prfCapabilityReported === null && result.platformAuthenticatorAvailable) {
        result.supported = true;
        result.reason = 'PRF support could not be confirmed; will attempt at auth time';
        return result;
    }
    if (!result.platformAuthenticatorAvailable) {
        result.reason = 'No platform authenticator available';
        result.platformHint = 'unsupported_browser';
    }
    return result;
};
export const registerPasskey = async (forceCreate, displayName, storage) => {
    if (!forceCreate && storage) {
        const existing = storage.getActiveCredentialId();
        if (existing)
            return { credentialId: existing };
    }
    if (typeof window === 'undefined')
        throw new Error('Passkey registration must run in a browser');
    if (!window.isSecureContext)
        throw new Error('Passkeys require HTTPS (secure context)');
    const challengeBytes = new Uint8Array(32);
    window.crypto.getRandomValues(challengeBytes);
    const userIdBytes = new Uint8Array(16);
    window.crypto.getRandomValues(userIdBytes);
    const prfSalt = getPasskeyPrfSalt();
    const existingIds = storage?.getAllCredentialIds() ?? [];
    const excludeCredentials = existingIds.map(id => ({
        type: 'public-key',
        id: base64URLStringToBuffer(id),
    }));
    const publicKey = {
        challenge: challengeBytes,
        rp: { name: 'Train', id: window.location.hostname },
        user: {
            id: userIdBytes,
            name: 'train-user',
            displayName: displayName?.trim() || 'Train user',
        },
        pubKeyCredParams: [{ type: 'public-key', alg: -7 }],
        excludeCredentials,
        authenticatorSelection: { residentKey: 'required', userVerification: 'required' },
        attestation: 'none',
        timeout: 60000,
        extensions: { prf: { eval: { first: prfSalt } } },
    };
    const credential = await navigator.credentials.create({ publicKey });
    if (!credential)
        throw new Error('Failed to create passkey credential');
    const credentialId = bufferToBase64URLString(credential.rawId);
    storage?.storeCredentialId(credentialId);
    const ext = credential.getClientExtensionResults?.() ?? {};
    const prfFirst = ext?.prf?.results?.first;
    if (prfFirst) {
        const ikm = new Uint8Array(prfFirst);
        const identitySalt = Buffer.from(IDENTITY_SALT, 'utf8');
        const key = Buffer.from(deriveKeyMaterial(ikm, identitySalt));
        return { credentialId, key };
    }
    return { credentialId };
};
export const deriveKeyWithPasskey = async (options, storage) => {
    const createIfMissing = options?.createIfMissing !== false;
    if (typeof window === 'undefined')
        throw new Error('Passkey auth must run in a browser');
    if (!window.isSecureContext)
        throw new Error('Passkeys require HTTPS (secure context)');
    const prfSalt = getPasskeyPrfSalt();
    const challengeBytes = new Uint8Array(32);
    window.crypto.getRandomValues(challengeBytes);
    const publicKey = {
        rpId: window.location.hostname,
        challenge: challengeBytes,
        userVerification: 'required',
        extensions: { prf: { eval: { first: prfSalt } } },
    };
    let cred = (await navigator.credentials.get({ publicKey }));
    if (!cred) {
        if (!createIfMissing)
            throw new Error('No passkey found for this site. Create one instead.');
        const result = await registerPasskey(true, undefined, storage);
        if (result.key)
            return { key: result.key, credentialId: result.credentialId };
        cred = (await navigator.credentials.get({ publicKey }));
        if (!cred)
            throw new Error('Passkey authentication was cancelled or no passkey is available');
    }
    const credentialId = bufferToBase64URLString(cred.rawId);
    const ext = cred.getClientExtensionResults?.() ?? {};
    const prfFirst = ext?.prf?.results?.first;
    if (!prfFirst)
        throw new Error('Passkey PRF extension not available in this browser/authenticator');
    const ikm = new Uint8Array(prfFirst);
    const identitySalt = Buffer.from(IDENTITY_SALT, 'utf8');
    const key = Buffer.from(deriveKeyMaterial(ikm, identitySalt));
    return { key, credentialId };
};
export const mapPasskeyError = (error) => {
    const msg = error instanceof Error ? error.message : String(error);
    const msgLower = msg.toLowerCase();
    if (msgLower.includes('prf'))
        return "Your device doesn't support secure key derivation. Try using a wallet instead.";
    if (msgLower.includes('no passkey found') || msgLower.includes('no credentials'))
        return 'No passkey found for this site. Would you like to create one?';
    if (msgLower.includes('cancelled') || msgLower.includes('canceled') || msgLower.includes('not allowed') || msgLower.includes('abort'))
        return 'Authentication cancelled. Try again when ready.';
    if (msgLower.includes('not supported') || msgLower.includes('security error'))
        return 'Passkeys are not supported in this browser. Try using a wallet instead.';
    if (msgLower.includes('timeout'))
        return 'Authentication timed out. Please try again.';
    return msg;
};
